import { createContext, useCallback, useContext, useState } from 'react'
import { removeAt, replaceAt } from '@10000/common'
import { Box } from '../widgets/index.js'

const testMode = window.location.search.includes('testMode')
const context = createContext({})
const nextId = (next => () => ++next)(0)
const defaultWrapper = ({ children }) => children
const transitions = {
  up: { pre: { top: '100vh' }, active: { top: 0 }, post: { top: '-100vh' } }
}

export const Stack = ({ initialStack = [], children, Wrapper = defaultWrapper, WrapperProps, limitHeight = true, active: parentActive }) => {
  const [stack, setStack] = useState(initialStack.map((x, i) => ({ ...x, id: nextId(), transition: 'up', active: i === (initialStack.length - 1) })))
  const parent = useContext(context)

  const pop = useCallback(id => {
    setStack(stack => {
      if(stack.length === 0) {
        return []
      }

      id = id || stack[stack.length - 1].id
      const initialIndex = stack.findIndex(x => x.id === id)

      if (initialIndex !== stack.length - 1) {
        // if the removed frame was not at the top, we can just pull it out and move on
        return removeAt(stack, initialIndex)

      } else {
        setTimeout(
          () => setStack(stack => removeAt(stack, stack.findIndex(x => x.id === id))),
          testMode ? 0 : 500
        )
        // otherwise we need to transition it out by setting the active frame to one below the top
        return stack.map((frame, i) => ({ ...frame, active: i === stack.length - 2 }))
      }
    })
  }, [])

  const push = useCallback((Component, { transition = 'up', props, onClose } = {}) => {
    const frames = Component instanceof Array
      ? Component.map(x => ({ id: nextId(), transition, ...x, active: false }))
      : [{ id: nextId(), Component, transition, props, onClose, active: false }]

    setStack(stack => [
      ...stack.map(x => ({ ...x, active: false })),
      ...frames
    ])
    setTimeout(() => {
      setStack(stack => replaceAt(
        stack,
        stack.length - 1,
        frame => ({ ...frame, active: true }))
      )
    }, 20)
  }, [])


  const close = useCallback((id, onClose, ...args) => {
    onClose?.(...args)
    pop(id)
  }, [pop])

  const replace = useCallback((Component, options) => {
    pop()
    push(Component, options)
  }, [pop, push])

  const replaceStack = useCallback((Component, options) => {
    setStack(stack => stack.slice(stack.length - 1, stack.length))
    replace(Component, options)
  }, [replace])

  return <context.Provider value={{ pop, push, replace, replaceStack, length: stack.length, parent }}>
    <Wrapper {...WrapperProps}>
      {stack.map(({ active, id, Component, props, transition, onClose }, i) => {
        const transitionPhase = active ? 'active'         // should only ever be one active frame
          : (i === stack.length - 1 && !active) ? 'pre'   // should only ever be one inactive after the active frame (after going "back") - always pre
          : 'post'                                        // otherwise always post

        return <Box
          key={id}
          position="absolute"
          fullHeight
          fullWidth
          maxHeight={limitHeight && '100dvh' /* dvh does not play nicely inside a scaled element, i.e. the display screen */}
          transition="all 0.5s"
          overflow="hidden"
          pointerEvents={transitionPhase !== 'active' && 'none'}
          {...transitions[transition][transitionPhase]}
        >
          <Component
            onClose={(...args) => close(id, onClose, ...args)}
            active={active && parentActive !== false}
            {...props}
          />
        </Box>
      })}
      {children}
    </Wrapper>
  </context.Provider>
}

export const useStack = () => useContext(context)
